import moment from "moment";
import { Store, StoreMenu, StoreOpenTime } from "redux_store/store/models";

import { CartItem } from "../../redux_store/cart/models";
import { MenuStructure } from "../../redux_store/menu/models";
import {
  GetOrderResponse,
  OrderResponse,
} from "../../services/api/order/model";
import {
  OrderOffset,
  OrderTime,
  StoresTimeZone,
} from "../../services/api/store/model";
import { CheckoutResponse } from "../checkout/model";
import { ErrorResponse } from "../error/models";

export enum OrderChannel {
  WEB = 2,
  APP = 1,
}

export enum OrderType {
  STANDARD = 1,
  GROUP_ORDER = 2,
  CATERING = 5,
}

export enum ErrorModalState {
  NONE = 1,
  ERROR = 2,
  CLOSED = 3,
}

export enum CollectionType {
  PICK_UP = "PICKUP",
  TABLE_SERVICE = "TABLE",
  DRIVE_THRU = "DRIVETHRU",
  DELIVERY = "DELIVERY",
}

export const getMenuChannelId = (collectionType?: CollectionType) => {
  return collectionType === CollectionType.DELIVERY ? 4 : 1;
};

export enum DropOffOption {
  MEET_AT_DOOR = "MEET_AT_DOOR",
  LEAVE_AT_DOOR = "LEAVE_AT_DOOR",
}

export enum TableServiceType {
  MANUAL = "MANUAL",
  QR = "QR",
}

export enum GroupOrderType {
  SINLGE = "SINGLE",
  SPLIT = "SPLIT",
}

export interface OrderTimeSetup {
  pickupDay: moment.Moment;
  pickupHour: number;
  pickupMinute: number;
  pickupAmPm: boolean;
  pickupASAP: boolean;
}

export interface OrderCollectionTypeProps {
  orderCollectionType: CollectionType;
}

export interface DeliveryOrder {
  quoteId?: string;
  fee?: number;
  pickupAddress?: DeliveryAddress;
  pickupGooglePlaceId?: string;
  dropoffAddress?: DeliveryAddress;
  dropoffGooglePlaceId?: string;
  dropoffSuiteAndFloor?: string;
  dropoffBusinessName?: string;
  dropoffOption: DropOffOption;
  dropoffNotes?: string;
  deliveryDuration?: {
    min: string | number;
    max: string | number;
  };
  trackingUrl?: string;
  trackingUrlExpiresAt?: string;
  dropoffPinCodeRequired?: boolean;
}

export interface SetupOrderProps {
  orderASAP: boolean;
  orderTime?: number | null;
  orderCollectionType: CollectionType;
  type?: OrderType;
  collectionStore?: Store;
  tableNumber?: string;
  tableInit?: TableServiceType;
  delivery?: DeliveryOrder;
  deliveryAddress?: DeliveryAddress;
}
export interface GetOrderProps {
  sessionId: string;
}

export interface OrderState {
  sessionId?: string;
  //TimeStamp
  orderInitialTime: number;
  //TimeStamp
  orderTime: number | null;
  orderCollectionType: CollectionType;
  orderASAP: boolean;
  email: string;
  includeCutlery: boolean;
  collectionStore?: Store;
  deliveryAddress?: DeliveryAddress;
  deliveryDuration?: {
    min: number;
    max: number;
  };
  tableNumber: string;
  driveThruOptionAvailable: boolean;
  getOrderLoading: boolean;
  getOrderByIdSuccess: boolean;
  getOrderError: ErrorResponse;
  displayGenericOrderError: ErrorModalState;
  updateOrderLoading: boolean;
  orderTimeUpdated: boolean;
  currentOrderId: string | null;
  guestOrderId: string | null;
  getOrderResponse: null | OrderResponse;
  userConfirmedOrderSetup: boolean;
  hasOrderPending: boolean;
  orderStatus: GetOrderResponse;
  currentOrders: CheckoutResponse[];
  recentOrders: CheckoutResponse[];
  tableInit: TableServiceType;
  showMoreAvailable: boolean;
  addToAccountLoading: boolean;
  addToAccountSuccess: boolean;
  generalError: string;
  getOrderByIdResponse: CheckoutResponse;
  getOrderByIdError: boolean;
  getAuthenticatedOrdersLoading: boolean;
  ratingOrder: RatedOrderProps;
  ignoreTimeChange: boolean;
  ratingValue: number | null;
  delivery: DeliveryOrder | null;
  showOrderUpdatedModal?: boolean;
  type: OrderType;
}

export interface AddToAccountError {
  message: string;
  code: number;
}

export interface SetCurrentOrRecentOrders {
  currentOrRecentOrders: CheckoutResponse[];
  clearCurrentAndRecentOrders: boolean;
}

export interface Product {
  productId: number;
  name: string;
  quantity: number;
  price: number;
}

export interface StoreAddress {
  streetAddress: string;
  city: string;
  state: string;
  postcode: string;
  latitude: number;
  longitude: number;
}

export interface GetAuthenticatedOrdersPayload {
  offset?: number;
}

export interface RatedOrderProps {
  cartSize: number | undefined;
  cartAmount: number | undefined;
  storeName: string | undefined;
  storeId: string | undefined;
  orderNumber?: string;
  orderId: string | undefined;
  orderType: string | undefined;
  productImage?: string;
  numberOfOtherProducts?: number;
  highestPriceProduct?: CartItem;
}

export interface AddressAutocompleteSuggestion {
  googlePlaceId: string;
  fullAddress: string;
}

export interface DeliveryAddress {
  fullAddress: string;
  googlePlaceId: string;
  addressLine1: string;
  addressLine2: string;
  suiteAndFloor: string;
  name: string;
  city: string;
  state: string;
  postCode: string;
  country: string;
  latitude: number;
  longitude: number;
}

export type DeliveryModalState = {
  AutoCompleteResult: AddressAutocompleteSuggestion[];
  OrderType: OrderType;
  OrderCollectionType?: CollectionType;
  DropoffAddress?: DeliveryAddress;
  PickupAddress?: DeliveryAddress;
  Apartment?: string;
  Business?: string;
  DropOffOption?: DropOffOption;
  DropOffOptionTitle?: string;
  DropoffPinCodeRequired?: boolean;
  Instructions?: string;
  DeliveryFee?: number;
  DeliveryDuration?: {
    min: string | number;
    max: string | number;
  };
  DeliveryQuoteId?: string;
  GooglePlaceId?: string;
  StoreDistance?: string;
  ChosenStore?: Store;
  orderTime?: number | null;
};

export interface PickupDetailsType {
  type?: OrderType;
  orderASAP: boolean;
  orderTime: number | null;
  orderCollectionType: CollectionType;
  tableNumber?: string;
  tableInit?: TableServiceType;
  menuStructure?: MenuStructure;
  menuOpenTime?: StoreMenu;
  timeSlots?: {
    timeZoneInfo: StoresTimeZone;
    orderTimes: OrderTime[];
    orderOffset: OrderOffset[];
  };
  storeOpenHours?: StoreOpenTime;
}

export interface PickupModalState {
  OrderType: OrderType;
  OrderCollectionType?: CollectionType;
  PickupDetails?: PickupDetailsType;
  ChosenStore?: Store;
}

export interface OrderTotals {
  totalBeforeDiscount: number;
  totalAfterDiscount: number;
}

export type ModalState = PickupModalState | DeliveryModalState;
